import styled from "@emotion/styled"
import { PHeading, PLinkPure, PText } from "@porsche-design-system/components-react"
import {
	spacingStaticXs,
	spacingStaticSm,
	spacingStaticMd,
	spacingFluidSm,
} from "@porsche-design-system/components-react/emotion"
import type { VinSearchResultView } from "@slfinrtl/ui-bff-routes"
import { FormattedMessage, useIntl } from "react-intl"
import { placeholderImageUrl } from "src/common/placeholderImageUrl.const"
import { ExteriorColorTile, Grid, GridItem, InteriorColorTile, List, Picture, Skeleton } from "src/components"
import { contentGridSize, imageGridSize } from "./gridSizes.const"
import { messages } from "./vinSearchResult.messages"
import { VinSearchResultMessage } from "./vinSearchResultMessage/VinSearchResultMessage"

interface VinSearchResultProps {
	view: VinSearchResultView | null
}

export const VinSearchResult = ({ view }: VinSearchResultProps) => {
	const { formatMessage } = useIntl()

	return view ? (
		<>
			<ResultMessageWrapper>
				<VinSearchResultMessage {...view} />
			</ResultMessageWrapper>
			<Grid $rowGap={spacingFluidSm}>
				<GridItem size={imageGridSize}>
					{view.imageUrl ? (
						<Picture aspectRatio="4:3" alt="" placeholderColor="transparent">
							<source srcSet={view.imageUrl} />
						</Picture>
					) : (
						<PlaceholderImage />
					)}
				</GridItem>
				<GridItem size={contentGridSize}>
					<List header={view.name}>
						{view.modelYear && (
							<List.Item label={formatMessage(messages.modelYearLabel)} value={view.modelYear} />
						)}
						{view.modelGeneration && (
							<List.Item label={formatMessage(messages.modelGeneration)} value={view.modelGeneration} />
						)}
						{view.exteriorColor && (
							<List.Item
								label={formatMessage(messages.exteriorColor)}
								value={
									<Center>
										{view.exteriorColor.hexCode && (
											<ExteriorColorTile color={view.exteriorColor.hexCode} glossy />
										)}
										<ColorName ellipsis title={view.exteriorColor.name}>
											{view.exteriorColor.name}
										</ColorName>
									</Center>
								}
							/>
						)}
						{view.rooftopColor && (
							<List.Item
								label={formatMessage(messages.roofColor)}
								value={
									<Center>
										{view.rooftopColor.hexCode && (
											<ExteriorColorTile glossy={false} color={view.rooftopColor.hexCode} />
										)}
										<ColorName ellipsis title={view.rooftopColor.name}>
											{view.rooftopColor.name}
										</ColorName>
									</Center>
								}
							/>
						)}
						{view.interiorColors && (
							<List.Item
								label={formatMessage(messages.interiorColor)}
								value={
									<Center>
										{view.interiorColors.seatColor && (
											<InteriorColorTile
												{...view.interiorColors}
												seatColor={view.interiorColors.seatColor}
											/>
										)}
										<ColorName ellipsis title={view.interiorColors.name}>
											{view.interiorColors.name}
										</ColorName>
									</Center>
								}
							/>
						)}
						{view.otherOwningDealership && (
							<List.Item
								label={formatMessage(messages.location)}
								value={
									<div>
										{view.otherOwningDealership.displayName &&
											(view.otherOwningDealership.homepage ? (
												<StyledLinkPure
													icon="external"
													alignLabel="start"
													href={view.otherOwningDealership.homepage}
													target="_blank"
												>
													{view.otherOwningDealership.displayName}
												</StyledLinkPure>
											) : (
												<PText>{view.otherOwningDealership.displayName}</PText>
											))}
										{view.otherOwningDealership.phone && (
											<PText>{view.otherOwningDealership.phone}</PText>
										)}
										{view.otherOwningDealership.email && (
											<StyledLinkPure
												icon="none"
												href={`mailto:${view.otherOwningDealership.email}`}
												dangerouslySetInnerHTML={{
													__html: view.otherOwningDealership.email.replace("@", "<wbr>@"),
												}}
											/>
										)}
									</div>
								}
							/>
						)}
					</List>
				</GridItem>
			</Grid>
		</>
	) : (
		<>
			{/* This Spacing makes that both containers (searchVinResult true/false) have the same height  */}
			<ResultMessageWrapper>
				<Skeleton.Text $width="0px" />
			</ResultMessageWrapper>

			<Grid $rowGap={spacingFluidSm}>
				<GridItem size={imageGridSize}>
					<PlaceholderImage />
				</GridItem>
				<GridItem size={contentGridSize}>
					<CenterVertically>
						<PHeading size="md">
							<FormattedMessage {...messages.noVehicleFoundHeadline} />
						</PHeading>
						<PText>
							<FormattedMessage {...messages.noVehicleFoundMessage} />
						</PText>
					</CenterVertically>
				</GridItem>
			</Grid>
		</>
	)
}

const ResultMessageWrapper = styled.div`
	margin: ${spacingStaticXs} 0 ${spacingStaticMd};
`

const CenterVertically = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	height: 100%;
`

const ColorName = styled(PText)`
	max-width: 100%;
	overflow: hidden;
`

const Center = styled.div`
	display: flex;
	align-items: center;
	gap: ${spacingStaticSm};
`

const StyledLinkPure = styled(PLinkPure)`
	word-break: break-word;
`

const PlaceholderImage = () => (
	<Picture aspectRatio="4:3" alt="placeholder">
		<source srcSet={placeholderImageUrl} />
	</Picture>
)
